// Pads: the direct-manipulation interface, and the study's reference
// condition. Geometry is a study variable (pad width and distance feed a
// Fitts regression), so the pads are hand-built DOM with exact pixel sizing
// rather than a widget library that would decide the layout for us.
//
// In study mode a "home" pad sits below the grid. Fitts' law is about
// movement time, so the clock starts when the finger *leaves* home, not when
// the cue appears -- otherwise reaction time lands in the regression
// intercept and makes it uninterpretable. Free play never shows it: the
// instrument should not look like an experiment.

import './pads.css'
import { SOUNDS } from '../engine/presets.js'

export const meta = {
  id: 'pads',
  label: 'Pads',
  order: 10,
  paramGroup: 'pads',
  blurb: 'tap a pad to play it; size, gap and count are parameters',
  studyCondition: true,
}

// Long enough to see on a 60Hz screen, short enough not to smear a roll.
const HIT_MS = 90

// Below this there is no room for a home pad without crowding the targets.
const MIN_PADS_FOR_HOME = 3

const columnsFor = (count, layout) =>
  layout === 'row' ? count : Math.ceil(Math.sqrt(count))

const centreOf = (rect) => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

const round1 = (n) => Math.round(n * 10) / 10

function emitter() {
  const fns = new Set()
  return {
    add(fn) { fns.add(fn); return () => fns.delete(fn) },
    fire(payload) { for (const fn of fns) fn(payload) },
    clear() { fns.clear() },
  }
}

export function mount({ api, root, params }) {
  const grid = document.createElement('div')
  grid.className = 'pads'

  const homeRow = document.createElement('div')
  homeRow.className = 'pads__home-row'

  const home = document.createElement('button')
  home.type = 'button'
  home.className = 'pad pad--home'
  home.setAttribute('aria-label', 'home')
  home.appendChild(Object.assign(document.createElement('span'), {
    className: 'pad__label',
    textContent: 'home',
  }))
  homeRow.appendChild(home)

  root.appendChild(grid)
  root.appendChild(homeRow)

  // pointerId -> pad element, so several fingers can hold several pads.
  const held = new Map()
  const hitTimers = new Map()

  const onsets = emitter()
  const cancels = emitter()

  let cuedSound = null
  let homeEnabled = false
  let homeWanted = null      // explicit override from study.setHome()
  let homePointerId = null
  let pads = []

  const homeHeld = () => homePointerId !== null

  // Study mode is not a schema flag, so infer it: either the study module
  // asked for the home pad outright, or the study group says a run is on.
  function studyRunning() {
    const s = params.group('study')
    return !!(s.logging || s.condition)
  }

  function syncHome(p = params.group('pads')) {
    homeEnabled = (homeWanted ?? studyRunning()) && p.count >= MIN_PADS_FOR_HOME
    homeRow.hidden = !homeEnabled
    if (!homeEnabled) homePointerId = null
  }

  function build(p) {
    grid.innerHTML = ''
    grid.style.setProperty('--pad-size', `${p.size}px`)
    grid.style.setProperty('--pad-gap', `${p.gap}px`)
    grid.style.setProperty('--pad-cols', String(columnsFor(p.count, p.layout)))
    grid.classList.toggle('pads--row', p.layout === 'row')
    homeRow.style.setProperty('--pad-size', `${p.size}px`)
    homeRow.style.setProperty('--pad-gap', `${p.gap}px`)

    pads = []
    for (let i = 0; i < p.count; i++) {
      const sound = SOUNDS[i % SOUNDS.length]
      const pad = document.createElement('button')
      pad.type = 'button'
      pad.className = 'pad'
      pad.dataset.sound = sound
      pad.style.setProperty('--pad-color', `var(--${sound})`)
      pad.setAttribute('aria-label', sound)
      if (p.labels) {
        const label = document.createElement('span')
        label.className = 'pad__label'
        label.textContent = sound
        pad.appendChild(label)
      }
      grid.appendChild(pad)
      pads.push(pad)
    }

    syncHome(p)
    // A rebuild throws away the elements the current cue lived on.
    if (cuedSound) applyCue(cuedSound)
  }

  // Feedback is a class the browser can paint on the next frame; nothing
  // waits on a transition, because a hit that "arrives late" reads as
  // latency in the instrument.
  function flash(pad) {
    pad.classList.remove('pad--hit')
    void pad.offsetWidth   // restart the flash if the pad is hit again mid-flash
    pad.classList.add('pad--hit')
    clearTimeout(hitTimers.get(pad))
    hitTimers.set(pad, setTimeout(() => pad.classList.remove('pad--hit'), HIT_MS))
  }

  function leaveHome(e) {
    if (homePointerId !== e.pointerId) return
    homePointerId = null
    home.classList.remove('pad--down')
    onsets.fire({ t: e.timeStamp, pointer_type: e.pointerType })
  }

  function onPointerDown(e) {
    if (e.target.closest('.pad--home')) {
      if (!homeEnabled) return
      e.preventDefault()
      api.unlock()
      homePointerId = e.pointerId
      home.classList.add('pad--down')
      // Touch pointers are implicitly captured to their target, which would
      // swallow the boundary event that *is* the movement onset.
      try { home.releasePointerCapture(e.pointerId) } catch { /* not captured */ }
      return
    }

    const pad = e.target.closest('.pad')
    if (!pad || held.has(e.pointerId)) return
    // Suppress the compatibility mouse events and the focus ring flash; the
    // pad is an instrument key, not a form control.
    e.preventDefault()
    api.unlock()

    held.set(e.pointerId, pad)
    api.trigger(pad.dataset.sound, {
      t: e.timeStamp,
      hit_x_px: e.clientX,
      hit_y_px: e.clientY,
      pointer_type: e.pointerType,
      multi_touch: held.size > 1,
    })
    flash(pad)
    pad.classList.add('pad--down')
    // Capture keeps the release with this pad even if the finger slides off.
    try { pad.setPointerCapture(e.pointerId) } catch { /* pad already gone */ }
  }

  function onPointerUp(e) {
    if (homePointerId === e.pointerId) { leaveHome(e); return }
    release(e)
  }

  function onPointerOut(e) {
    // The finger sliding off home is movement onset just as much as a lift.
    if (homePointerId === e.pointerId && e.target.closest('.pad--home')) leaveHome(e)
  }

  function onPointerCancel(e) {
    // iOS steals gestures. A stolen trial has to be flagged, not averaged in.
    cancels.fire({ t: e.timeStamp, pointer_type: e.pointerType, pointer_cancelled: true })
    if (homePointerId === e.pointerId) { leaveHome(e); return }
    release(e)
  }

  function release(e) {
    const pad = held.get(e.pointerId)
    if (!pad) return
    held.delete(e.pointerId)
    pad.classList.remove('pad--down')
  }

  function onKeyDown(e) {
    if (e.repeat || (e.key !== ' ' && e.key !== 'Enter')) return
    const pad = e.target.closest('.pad')
    if (!pad || pad === home) return
    e.preventDefault()
    api.unlock()
    api.trigger(pad.dataset.sound, { t: e.timeStamp, pointer_type: 'key' })
    flash(pad)
  }

  const noContext = (e) => e.preventDefault()

  root.addEventListener('pointerdown', onPointerDown)
  root.addEventListener('pointerup', onPointerUp)
  root.addEventListener('pointerout', onPointerOut)
  root.addEventListener('pointercancel', onPointerCancel)
  root.addEventListener('lostpointercapture', release)
  root.addEventListener('keydown', onKeyDown)
  root.addEventListener('contextmenu', noContext)

  function padsFor(sound) {
    return pads.filter((pad) => pad.dataset.sound === sound)
  }

  function applyCue(sound) {
    for (const pad of pads) pad.classList.toggle('pad--cue', pad.dataset.sound === sound)
  }

  build(params.group('pads'))

  const offPads = params.onGroupChange('pads', (p) => {
    // Rebuilding on every change keeps one code path; geometry() re-measures
    // from the DOM afterwards, so a mid-study size change is honest.
    held.clear()
    build(p)
  })
  const offStudy = params.onGroupChange('study', () => syncHome())

  return {
    unmount() {
      offPads()
      offStudy()
      root.removeEventListener('pointerdown', onPointerDown)
      root.removeEventListener('pointerup', onPointerUp)
      root.removeEventListener('pointerout', onPointerOut)
      root.removeEventListener('pointercancel', onPointerCancel)
      root.removeEventListener('lostpointercapture', release)
      root.removeEventListener('keydown', onKeyDown)
      root.removeEventListener('contextmenu', noContext)
      for (const t of hitTimers.values()) clearTimeout(t)
      hitTimers.clear()
      held.clear()
      onsets.clear()
      cancels.clear()
      pads = []
      root.innerHTML = ''
    },

    study: {
      // Force the home pad on or off; pass null to go back to inferring it
      // from the study params.
      setHome(on) { homeWanted = on; syncHome() },
      get hasHome() { return homeEnabled },

      // Movement onset: the finger leaving home. cb({ t, pointer_type }).
      onOnset(cb) { return onsets.add(cb) },

      // Trials where the platform took the gesture away.
      onCancel(cb) { return cancels.add(cb) },

      cue(sound) {
        cuedSound = sound
        applyCue(sound)
        // A trial that began with a finger already down, or not resting on
        // home, has no usable movement time.
        return { down_before_cue: held.size > 0 || (homeEnabled && !homeHeld()) }
      },

      clearCue() {
        cuedSound = null
        for (const pad of pads) pad.classList.remove('pad--cue')
      },

      // Measured, never derived from the params: the regression has to use
      // what was actually on screen, including any CSS clamping.
      geometry(sound) {
        const candidates = padsFor(sound)
        if (candidates.length === 0) return {}

        const homeRect = homeEnabled ? home.getBoundingClientRect() : null
        const origin = homeRect ? centreOf(homeRect) : centreOf(grid.getBoundingClientRect())

        // With count > 4 a sound appears on several pads; the nearest one is
        // the target a participant actually moves to.
        let best = null
        for (const pad of candidates) {
          const rect = pad.getBoundingClientRect()
          const c = centreOf(rect)
          const d = distance(origin, c)
          if (!best || d < best.d) best = { d, rect, c }
        }

        return {
          pad_width_px: round1(best.rect.width),
          pad_distance_px: round1(best.d),
          target_cx_px: round1(best.c.x),
          target_cy_px: round1(best.c.y),
          home_cx_px: homeRect ? round1(origin.x) : null,
          home_cy_px: homeRect ? round1(origin.y) : null,
          has_home: homeEnabled,
          region: sound,
        }
      },
    },
  }
}
